import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { UserService } from '@core/services/user.service';
import { CustomEventService } from '@shared/services/custom-event.service';
import { DashboardService } from '@shared/services/dashboard.service';
import { DeveloperService } from '@shared/services/developer.service';
import { TeamService } from '@shared/services/team.service';

import { DashboardSettingsModalComponent } from '@shared/components/modals/dashboard-settings-modal/dashboard-settings-modal.component';
import { DashboardTypeSelectionModalComponent } from '@shared/components/modals/dashboard-type-selection-modal/dashboard-type-selection-modal.component';
import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { DashboardPermissionEnum } from '@shared/enums/dashboard-permission.enum';
import { BaseSkeletonEnum } from '@shared/enums/skeleton.enum';

import { getUserLimitations } from '@shared/utils/limitation.util';
import { fullNamePropertyCreator } from '@shared/utils/object-mapper.util';

import { DEFAULT_PAGE_SIZE } from '@shared/variables/constant-variable';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { StorageService } from "@core/services/storage.service";

@UntilDestroy()
@Component({
  selector: 'app-dashboards',
  templateUrl: './dashboards.component.html'
})
export class DashboardsComponent implements OnInit, OnDestroy {

  DESCRIPTION_LENGTH = 100;
  NAME_LENGTH = 20;
  PAGE_SIZE = DEFAULT_PAGE_SIZE;


  loading: boolean;
  typeSelectionModal: NzModalRef;
  dashboardSettingsModal: NzModalRef;
  topFavoriteDashboards: Dashboards.DashboardsModel[] = [];
  dashboards: Dashboards.DashboardsModel[] = [];
  displayDashboards: Dashboards.DashboardsModel[] = [];
  searchValue = '';
  fields: any;
  displayIndex: number;

  activeUser: ActiveUser.ActiveUserModel;
  userLimited: User.Limitations;
  licenseLimitations: LicenseLimitations.LicenseLimitationsModel;

  customEventSubscription: Subscription;

  baseSkeletonEnum = BaseSkeletonEnum;
  pageIndex: any;

  @ViewChild('dashboardCarousel', { static: false }) private dashboardCarousel: ElementRef;
  constructor(
    private customEventService: CustomEventService,
    private dashboardService: DashboardService,
    private developerService: DeveloperService,
    private teamService: TeamService,
    private userService: UserService,
    private router: Router,
    private modalService: NzModalService,
    private storageService: StorageService,
  ) {
    this.pageIndex = this.storageService.get('active_page_dashboard');
    if (this.pageIndex === null || this.pageIndex === undefined) {
      this.pageIndex = 1;
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.activeUser = this.userService.activeUser;

    this.getDashboardList();

    getUserLimitations(this.userService).then(response => {
      this.licenseLimitations = response;
    }).catch(error => {
      console.log('error while limitations data', error);
    });

    if (this.activeUser.limitedEdition) {
      getUserLimitations(this.userService).then(response => {
        this.userLimited = response;
      }).catch(error => {
        console.log('error while limitations data', error);
      });
    }

    this.customEventSubscription = this.customEventService.eventAnnounced$.subscribe(
      ({ action, data }) => {
        if (action === ActionTypesEnum.CLOSE_MODAL) {
          if (this.dashboardSettingsModal) {
            this.dashboardSettingsModal.close();
          }
        }

        if (action === ActionTypesEnum.DASHBOARD_TYPE_SELECTED) {
          this.typeSelectionHandler(data);
        }

        if (action === ActionTypesEnum.ADD_DASHBOARD) {
          this.addHandler(data);
        }

        if (action === ActionTypesEnum.DASHBOARD_FORM_FIELDS) {
          this.getDashboardFields();
        }
      });
  }

  getDashboardList(): void {
    this.dashboardService.list()
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.loading = false;

        this.topFavoriteDashboards = response.filter(item => item.favorite);
        this.dashboards = response;
        this.displayDashboards = this.dashboards;

      }, error => {
        this.loading = false;
        console.log('error while fetching dashboards', error);
      });
  }

  onCarousel(event: any, direction: string): void {
    const carousel: any = this.dashboardCarousel;

    this.displayIndex = Math.floor( this.topFavoriteDashboards.length / 3);
    if (this.topFavoriteDashboards.length % 3 === 0) {
      this.displayIndex = this.displayIndex - 1
    }

    switch (direction) {
      case 'left':
        carousel.pre();
      break;
      case 'right':
        carousel.next();
      break;
    }
  }

  search(): void {
    if (!this.searchValue.length) {
      this.displayDashboards = [...this.dashboards];
      return;
    }

    const searchFunction = (dashboard) =>
      this.searchValue?.length && dashboard.name.toLowerCase().includes(this.searchValue.toLowerCase());

    this.displayDashboards = this.dashboards.filter(searchFunction);
  }

  typeSelectionHandler(dashboardType) {
    this.typeSelectionModal.close();
    if (dashboardType === 'custom') {
      this.addDashboardSettings();
    }
  }

  addDashboard(): void {
    if(this.licenseLimitations?.maxDashboardCount <= this.dashboards.length) {
      return;
    } else {
      if (!this.activeUser.limitedEdition || this.dashboards.length < this.userLimited?.dashboard) {
        this.typeSelectionModal = this.modalService.create({ 
          nzMaskClosable: false,
          nzContent: DashboardTypeSelectionModalComponent,
          nzWidth: '450px',
          nzFooter: null
        });
      }
    }

  }

  addHandler(data: Dashboards.DashboardsModel[]): void {
    const innerComponent = this.dashboardSettingsModal.getContentComponent();

    innerComponent.loading = true;

    this.dashboardService.create(data)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        const { id } = response;

        setTimeout((): void => {
          innerComponent.loading = false;
        }, 1000);

        setTimeout((): void => {
          this.dashboardSettingsModal.destroy();
          this.router.navigate([`/dashboards/${id}`]);
        }, 2000);
      }, error => {
        innerComponent.loading = false;
        console.log('error while creating dashboard', error);
      });
  }

  addDashboardSettings(): void {
    this.dashboardSettingsModal = this.modalService.create({ 
          nzMaskClosable: false,
      nzContent: DashboardSettingsModalComponent,
      nzComponentParams: {
        mode: 'add',
        dashboards: this.dashboards
      },
      nzWidth: '500px',
      nzFooter: null
    });
  }

  getDashboardFields(): void {
    const innerComponent = this.dashboardSettingsModal.getContentComponent();

    innerComponent.fieldsLoading = true;
    this.dashboardService.fields()
      .subscribe(fields => {
        innerComponent.fieldsLoading = false;
        const { users, teams, permissions } = fields;

        innerComponent.users = users.map(item => fullNamePropertyCreator(item));
        innerComponent.teams = teams;
        innerComponent.permissions = permissions;
        innerComponent.selectedRole = permissions.find(item => item.key === DashboardPermissionEnum.VIEW);
      }, error => {
        innerComponent.fieldsLoading = false;
        console.log('error occurred while fetching dashboard fields', error);
      });
  }

  favoriteDashboard(id: string, $event: any): void {
    $event.stopPropagation();
    this.dashboardService.favoriteDashboard(id)
      .pipe(untilDestroyed(this))
      .subscribe(_ => {
        this.getDashboardList();
      }, error => {
        console.log('error while updating favorite dashboard', error);
      });
  }

  pageIndexChange($event): void {
    this.pageIndex = $event;
    this.storageService.set('active_page_dashboard', $event);
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
    this.customEventSubscription.unsubscribe();
  }

  upgradeConfirm(): void {
    window.open('https://oobeya.io/pricing', '_blank')
  }

  upgradeLicense(): void {
    window.open('https://oobeya.io/contact-sales/', '_blank')
  }
}
